/**
 * PickCell – Read-only pick display (like 1X2 odds card): label + odds, or "?" when unknown.
 * Use "?" for: my picks I didn't put yet (accept/decline), and his picks he didn't put yet (pending).
 * When it's "your turn" and cell shows "?", optional onPress opens the accept modal (use Pressable with pressed animation).
 * Theme-aware: wrong state is border-only (red border, same background as default).
 */

import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform, type ViewStyle } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { getChallengeCardStyles } from '../../src/challenges/styles/challengeCard.styles';
import { useTheme } from '../../src/theme/useTheme';

const UNKNOWN_PLACEHOLDER = '?';

export interface PickCellProps {
  /** Pick label (e.g. "Yes", "1", "Over 2.5"). When undefined, show "?". */
  label?: string;
  /** Odds value (e.g. 1.80). When undefined, show "?". */
  odds?: number;
  /** When true, show correct state (green border). */
  isCorrect?: boolean;
  /** When true, show wrong state (red border/tint; match finished, pick was incorrect). */
  isWrong?: boolean;
  /** Optional extra container style (e.g. wider on large screens). */
  containerStyle?: ViewStyle;
  /** When provided and cell shows "?", the cell is pressable (e.g. opens accept modal). */
  onPress?: () => void;
  /** Test ID for testing */
  testID?: string;
}

export const PickCell: React.FC<PickCellProps> = ({
  label,
  odds,
  isCorrect,
  isWrong,
  containerStyle,
  onPress,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getChallengeCardStyles(theme), [theme]);
  const hasPick = label != null || odds != null;
  const isUnknownAndPressable = !hasPick && onPress != null;

  const labelStyle = isWrong ? styles.pickCellLabelWrong : styles.pickCellLabel;
  const oddsStyle = isWrong ? styles.pickCellOddsWrong : styles.pickCellOdds;

  const cellStyle = [
    styles.pickCell,
    isCorrect && styles.pickCellCorrect,
    isWrong && styles.pickCellWrong,
    containerStyle,
  ];

  const content = !hasPick ? (
    <Text style={styles.pickCellUnknown}>{UNKNOWN_PLACEHOLDER}</Text>
  ) : (
    <>
      <Text style={labelStyle} numberOfLines={1}>
        {label ?? UNKNOWN_PLACEHOLDER}
      </Text>
      <Text style={oddsStyle} numberOfLines={1}>
        {odds != null ? odds.toFixed(2) : UNKNOWN_PLACEHOLDER}
      </Text>
    </>
  );

  if (isUnknownAndPressable) {
    return (
      <Pressable
        style={({ pressed, hovered }) => [
          ...cellStyle,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.pickCellPressed,
          hovered && Platform.OS === 'web' && styles.pickCellPressed,
        ]}
        onPress={onPress}
        testID={testID}
        accessibilityLabel="Make your pick"
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View style={cellStyle} testID={testID}>
      {content}
    </View>
  );
};
